"use client";

import { useEffect } from "react";
import { recentLinkWillRestoreScroll } from "./RecentLinkHighlighter";

// Next never sets history.scrollRestoration, so the browser restores the offset
// itself the moment popstate fires — while the DOM still holds the page being
// left. The router's own update runs inside startTransition and commits after
// that, so on any route whose two states differ in height the browser clamps the
// offset to the shorter page and the taller one arrives too late to correct it.
// A brand page filtered down to one category is exactly that shape: the hero and
// everything pinned above the list step aside, and coming back lands short.
//
// So the restore is taken over here: the browser is told to keep its hands off,
// every entry carries an id, and the offset is re-applied once the page it
// belongs to is actually tall enough to hold it.

const KEY_FIELD = "__vScrollKey";
const OFFSET_PREFIX = "scroll-restore";
const COUNTER_KEY = "scroll-restore:counter";

// How long the restored page is given to reach the height it needs. Landing
// components and their images settle over the frames after a commit, so the
// offset is not reachable on the first one.
const SETTLE_BUDGET_MS = 1200;

// Below this the offset is not worth chasing: the page is near its top anyway.
const MIN_OFFSET = 2;

// A page rarely comes back to the exact pixel — a lazy image can resolve to a
// slightly different box — so "as tall as it was" allows for a little less.
const HEIGHT_TOLERANCE = 200;

function readCounter() {
    try {
        return Number.parseInt(sessionStorage.getItem(COUNTER_KEY) ?? "0", 10) || 0;
    } catch {
        return 0;
    }
}

// The id lives in history.state so two entries on the same URL stay apart. Next
// passes an object that already carries __NA straight to the native call, so its
// own router fields survive being spread here.
function ensureKey(): number | null {
    try {
        const state = window.history.state;
        const existing = state?.[KEY_FIELD];
        if (typeof existing === "number") return existing;

        const key = readCounter() + 1;
        sessionStorage.setItem(COUNTER_KEY, String(key));
        window.history.replaceState({ ...(state ?? {}), [KEY_FIELD]: key }, "");
        return key;
    } catch {
        return null;
    }
}

function currentKey(): number | null {
    const key = window.history.state?.[KEY_FIELD];
    return typeof key === "number" ? key : null;
}

function currentAddress() {
    return `${window.location.pathname}${window.location.search}`;
}

// Filed twice: under the entry id, and under the address. The id is the precise
// one — two entries on the same address keep their own offsets — but nothing
// guarantees an entry still carries it, and an address is always available.
//
// The height goes with the offset because the offset only means anything on a
// page that size. Returning to a page that is still missing the sections above
// its list and applying the number there would land in the middle of the
// products, which is what the shopper saw as a jump before the real position
// arrived.
function saveOffset(key: number | null, offset: number) {
    const value = `${Math.round(offset)}:${document.documentElement.scrollHeight}`;
    try {
        if (key !== null) sessionStorage.setItem(`${OFFSET_PREFIX}:${key}`, value);
        sessionStorage.setItem(`${OFFSET_PREFIX}:url:${currentAddress()}`, value);
    } catch { /* Storage can be unavailable in restricted browsing modes. */ }
}

function readOffset(key: number | null) {
    try {
        const value = (key !== null ? sessionStorage.getItem(`${OFFSET_PREFIX}:${key}`) : null)
            ?? sessionStorage.getItem(`${OFFSET_PREFIX}:url:${currentAddress()}`);
        if (value === null) return null;

        const [rawOffset, rawHeight] = value.split(":");
        const offset = Number.parseInt(rawOffset, 10);
        if (!Number.isFinite(offset)) return null;

        const height = Number.parseInt(rawHeight, 10);
        return { offset, height: Number.isFinite(height) ? height : 0 };
    } catch {
        return null;
    }
}

export default function ScrollRestorer() {
    useEffect(() => {
        if (typeof window === "undefined" || !("scrollRestoration" in window.history)) return;

        const previousMode = window.history.scrollRestoration;
        window.history.scrollRestoration = "manual";

        let restoreFrame = 0;
        let scrollFrame = 0;
        let releaseAnchor: (() => void) | null = null;

        const stopRestoring = () => {
            releaseAnchor?.();
            releaseAnchor = null;
            if (!restoreFrame) return;
            window.cancelAnimationFrame(restoreFrame);
            restoreFrame = 0;
        };

        // Two things move the page while the taller version of it arrives, and both
        // have to be held off until it has.
        //
        // The first is the browser: when the hero and the sections above the list are
        // inserted, everything below them shifts down, and scroll anchoring silently
        // adds that height to the offset to keep the anchored element still. Measured
        // on a brand page, that turned a correctly restored 1586 into 4715 — exactly
        // the 3128px the two versions differ by. overflow-anchor: none switches it off
        // for the moment the page is growing.
        //
        // The second is the growth itself: setting the offset while the document is
        // still the shorter version lands on a position that means something else once
        // it is whole. So the target is only applied once the page can actually hold
        // it, and it keeps being re-applied until the height stops moving.
        const restore = ({ offset: target, height: savedHeight }: { offset: number; height: number }) => {
            stopRestoring();

            const html = document.documentElement;
            const previousAnchor = html.style.overflowAnchor;
            html.style.overflowAnchor = "none";
            releaseAnchor = () => { html.style.overflowAnchor = previousAnchor; };

            const deadline = Date.now() + SETTLE_BUDGET_MS;

            // Nothing is touched until the page is back to the size it was when the
            // offset was written, so the shopper never sees it applied to a page that
            // means something else. Past the deadline it is applied regardless: a page
            // whose content genuinely changed will never reach that height again.
            //
            // The watch then runs for the whole budget rather than stopping the moment
            // the offset first fits. The page reaches that state a few frames before
            // the sections above the list arrive, and letting go there is enough for
            // the growth to carry the offset away again.
            const tick = () => {
                const height = html.scrollHeight;
                const grown = savedHeight <= 0 || height >= savedHeight - HEIGHT_TOLERANCE;

                if (grown && height - window.innerHeight >= target && Math.abs(window.scrollY - target) > MIN_OFFSET) {
                    window.scrollTo(0, target);
                }

                if (Date.now() >= deadline) {
                    stopRestoring();
                    return;
                }

                restoreFrame = window.requestAnimationFrame(tick);
            };

            restoreFrame = window.requestAnimationFrame(tick);
        };

        // Whatever the shopper does with the page outranks putting them back on it. A key
        // press is not one of those: Alt+Left and Backspace are how a keyboard goes back.
        const abort = () => stopRestoring();

        // The id is handed out on the first scroll rather than on every navigation:
        // an entry nobody scrolled has nothing worth restoring, and this needs no
        // hook that would pull the whole shell into a Suspense boundary.
        const onScroll = () => {
            if (scrollFrame) return;
            scrollFrame = window.requestAnimationFrame(() => {
                scrollFrame = 0;
                if (restoreFrame) return;
                saveOffset(ensureKey(), window.scrollY);
            });
        };

        // A list that recorded the card the shopper clicked restores itself against
        // that card, which lands truer than a raw offset because the rows above it
        // can have changed height. Two restores would fight, so this one stands
        // down for it — but only for a list that really does move the page. A scope
        // that just pulses the card leaves the offset to this one.
        const onPopState = () => {
            const saved = readOffset(currentKey());
            if (saved === null || saved.offset <= MIN_OFFSET) return;
            if (recentLinkWillRestoreScroll()) return;
            restore(saved);
        };

        // A reload used to be the browser's job too, and manual mode hands it back.
        const initial = readOffset(currentKey());
        if (initial !== null && initial.offset > MIN_OFFSET && !recentLinkWillRestoreScroll()) restore(initial);

        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("popstate", onPopState);
        window.addEventListener("wheel", abort, { passive: true });
        window.addEventListener("touchstart", abort, { passive: true });
        window.addEventListener("pointerdown", abort, { passive: true });

        return () => {
            window.history.scrollRestoration = previousMode;
            stopRestoring();
            if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("popstate", onPopState);
            window.removeEventListener("wheel", abort);
            window.removeEventListener("touchstart", abort);
            window.removeEventListener("pointerdown", abort);
        };
    }, []);

    return null;
}
